import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  useGetPartnerByIdQuery,
  useUpdatePartnerMutation,
  useDeletePartnerMutation,
} from "@/features/api/partnersApi";

const EditPartner = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: partnerData, isLoading: partnerLoading } = useGetPartnerByIdQuery(id, {
    skip: !id,
    refetchOnMountOrArgChange: true,
  });

  const [updatePartner, { isLoading: updating }] = useUpdatePartnerMutation();
  const [deletePartner] = useDeletePartnerMutation();

  const [input, setInput] = useState({
    fullName: "",
    email: "",
    phone: "",
    orgName: "",
    orgWebsite: "",
    partnershipTypes: [],
    orgDescription: "",
    whyPartner: "",
    preferredContact: "",
    candidatesSent: "",
    comments: "",
    consent: false,
    status: "pending",
  });

  useEffect(() => {
    if (partnerData) {
      setInput({
        fullName: partnerData.fullName || "",
        email: partnerData.email || "",
        phone: partnerData.phone || "",
        orgName: partnerData.orgName || "",
        orgWebsite: partnerData.orgWebsite || "",
        partnershipTypes: partnerData.partnershipTypes || [],
        orgDescription: partnerData.orgDescription || "",
        whyPartner: partnerData.whyPartner || "",
        preferredContact: partnerData.preferredContact || "",
        candidatesSent: partnerData.candidatesSent || "",
        comments: partnerData.comments || "",
        consent: partnerData.consent || false,
        status: partnerData.status || "pending",
      });
    }
  }, [partnerData]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === "checkbox" && name === "partnershipTypes") {
      setInput((prev) => {
        const alreadySelected = prev.partnershipTypes.includes(value);
        return {
          ...prev,
          partnershipTypes: alreadySelected
            ? prev.partnershipTypes.filter((item) => item !== value)
            : [...prev.partnershipTypes, value],
        };
      });
    } else if (type === "checkbox") {
      setInput((prev) => ({ ...prev, [name]: checked }));
    } else {
      setInput((prev) => ({ ...prev, [name]: value }));
    }
  };

  const updatePartnerHandler = async () => {
    if (!input.consent) {
      toast.error("Please provide consent to be contacted.");
      return;
    }

    try {
      await updatePartner({
        id,
        ...input,
        candidatesSent: Number(input.candidatesSent) || 0,
      }).unwrap();
      toast.success("Partner updated successfully.");
      navigate("/admin/partner-list");
    } catch (err) {
      toast.error("Failed to update partner.");
      console.error(err);
    }
  };

  const handleDeletePartner = async () => {
    if (!window.confirm("Are you sure you want to delete this partner?")) return;

    try {
      await deletePartner(id).unwrap();
      toast.success("Partner deleted successfully.");
      navigate("/admin/partner-list");
    } catch (err) {
      toast.error("Failed to delete partner.");
      console.error(err);
    }
  };

  if (partnerLoading) return <Loader2 className="h-4 w-4 animate-spin mx-auto mt-10" />;

  if (!partnerData)
    return <p className="text-red-500 text-center mt-10">Error: Partner not found.</p>;

  return (
    <div className="max-w-3xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Edit Partner Details</h1>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          updatePartnerHandler();
        }}
        className="space-y-6"
      >
        {/* Full Name */}
        <div>
          <Label htmlFor="fullName">1. Full Name:</Label>
          <Input
            id="fullName"
            name="fullName"
            type="text"
            placeholder="Enter full name"
            value={input.fullName}
            onChange={handleInputChange}
            required
            className="mt-1"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">2. Email Address:</Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter email address"
            value={input.email}
            onChange={handleInputChange}
            required
            className="mt-1"
          />
        </div>

        {/* Phone */}
        <div>
          <Label htmlFor="phone">3. Phone Number:</Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            placeholder="Enter phone number"
            value={input.phone}
            onChange={handleInputChange}
            required
            className="mt-1"
          />
        </div>

        {/* Organization Name */}
        <div>
          <Label htmlFor="orgName">4. Organization Name:</Label>
          <Input
            id="orgName"
            name="orgName"
            type="text"
            placeholder="Enter organization name"
            value={input.orgName}
            onChange={handleInputChange}
            className="mt-1"
          />
        </div>

        {/* Organization Website */}
        <div>
          <Label htmlFor="orgWebsite">5. Organization Website:</Label>
          <Input
            id="orgWebsite"
            name="orgWebsite"
            type="url"
            placeholder="Enter organization website URL"
            value={input.orgWebsite}
            onChange={handleInputChange}
            className="mt-1"
          />
        </div>

        {/* Partnership Types */}
        <div>
          <Label>6. Type of Partnership Interested In:</Label>
          <div className="flex flex-col gap-2 pl-4 mt-1">
            {[
              "Study Abroad Consultancy",
              "Work Permit Services",
              "Visitor Visa Services",
              "English Language Teaching",
              "Other",
            ].map((type) => (
              <label key={type} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  name="partnershipTypes"
                  value={type}
                  checked={input.partnershipTypes.includes(type)}
                  onChange={handleInputChange}
                  className="h-4 w-4"
                />
                {type}
              </label>
            ))}
          </div>
        </div>

        {/* Organization Description */}
        <div>
          <Label htmlFor="orgDescription">7. Describe Your Organization:</Label>
          <textarea
            id="orgDescription"
            name="orgDescription"
            placeholder="Provide a brief description of your organization and its services"
            value={input.orgDescription}
            onChange={handleInputChange}
            rows={4}
            className="w-full border border-gray-300 p-2 rounded mt-1"
          />
        </div>

        {/* Why Partner */}
        <div>
          <Label htmlFor="whyPartner">8. Why Do You Want to Partner With Us?</Label>
          <textarea
            id="whyPartner"
            name="whyPartner"
            placeholder="Share your reasons for wanting to partner with us"
            value={input.whyPartner}
            onChange={handleInputChange}
            rows={4}
            className="w-full border border-gray-300 p-2 rounded mt-1"
          />
        </div>

        {/* Preferred Contact */}
        <div>
          <Label>9. Preferred Communication Method:</Label>
          <div className="flex gap-6 pl-4 mt-1">
            {["Email", "Phone", "Video Call"].map((method) => (
              <label key={method} className="flex items-center gap-2">
                <input
                  type="radio"
                  name="preferredContact"
                  value={method}
                  checked={input.preferredContact === method}
                  onChange={handleInputChange}
                  className="h-4 w-4"
                />
                {method}
              </label>
            ))}
          </div>
        </div>

        {/* Candidates Sent */}
        <div>
          <Label htmlFor="candidatesSent">
            10. Number of Candidates Sent Abroad in the Past Year:
          </Label>
          <Input
            id="candidatesSent"
            name="candidatesSent"
            type="number"
            min="0"
            placeholder="Enter total candidates sent in last 12 months"
            value={input.candidatesSent}
            onChange={handleInputChange}
            className="mt-1"
          />
        </div>

        {/* Additional Comments */}
        <div>
          <Label htmlFor="comments">11. Additional Comments or Questions:</Label>
          <textarea
            id="comments"
            name="comments"
            placeholder="Any other information you would like to provide"
            value={input.comments}
            onChange={handleInputChange}
            rows={3}
            className="w-full border border-gray-300 p-2 rounded mt-1"
          />
        </div>

        {/* Consent */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            name="consent"
            id="consent"
            checked={input.consent}
            onChange={handleInputChange}
            className="h-4 w-4"
          />
          <Label htmlFor="consent" className="select-none">
            12. I consent to being contacted regarding partnership opportunities.
          </Label>
        </div>

        {/* Status (optional display) */}
        <div>
          <Label>Status</Label>
          <div className="flex items-center gap-4 mt-2">
            {["pending", "approved", "rejected"].map((status) => (
              <div key={status} className="flex items-center gap-2">
                <input
                  type="radio"
                  id={status}
                  name="status"
                  value={status}
                  checked={input.status === status}
                  onChange={handleInputChange}
                />
                <Label htmlFor={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Label>
              </div>
            ))}
          </div>
        </div>

        {/* Buttons */}
        <div className="flex space-x-4 pt-4">
          <Button
            type="button"
            onClick={() => navigate("/admin/partner-list")}
            disabled={updating}
            variant="outline"
          >
            Back to List
          </Button>
          <Button type="submit" disabled={updating}>
            {updating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin inline" /> Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={handleDeletePartner}
            disabled={updating}
          >
            Delete Partner
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditPartner;
